import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { Provider, useSelector } from "react-redux";
import { store, persistor } from "./store";
import { PersistGate } from "redux-persist/integration/react";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "./components/Layout";
import PublicLayout from "./components/PublicLayout";
import Login from "./pages/auth/Login";
import ResetPassword from "./pages/auth/ResetPassword";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import RolesList from "./pages/roles/RolesList";
import CreateRole from "./pages/roles/CreateRole";
import EditRole from "./pages/roles/EditRole";
import UpdateProfile from "./pages/profile/UpdateProfile";
import UpdatePassword from "./pages/profile/UpdatePassword";
import TwoFactorAuth from "./pages/profile/TwoFactorAuth";
import ValidateTelegram from "./pages/profile/ValidateTelegram";
import ModeratorsList from "./pages/moderators/ModeratorsList";
import AddModerator from "./pages/moderators/AddModerator";
import EditModerator from "./pages/moderators/EditModerator";
import UpdateModeratorPassword from "./pages/moderators/UpdateModeratorPassword";
import { setTokenGetter } from "./api/axios";
import UsersList from "./pages/users/UsersList";
import UserIncome from "./pages/users/UserIncome";
import UserStatement from "./pages/users/UserStatement";
import UserStats from "./pages/users/UserStats";
import AddUser from "./pages/users/AddUser";
import UpdateUserPassword from "./pages/users/UpdateUserPassword";
import ViewUserStatus from "./pages/users/ViewUserStatus";
import EditUser from "./pages/users/EditUser";
import SponsorBonusReport from "./pages/reports/SponsorBonusReport";
import SponsorBonusLevelWiseReport from "./pages/reports/SponsorBonusLevelWiseReport";
import WalletStatementReport from "./pages/reports/WalletStatementReport";
import GUSDWallets from "./pages/wallets/GUSDWallets";
import BFMWallets from "./pages/wallets/BFMWallets";
import APPCreditWallets from "./pages/wallets/APPCreditWallets";
import BalanceAdjustments from "./pages/wallets/BalanceAdjustments";
import TrustLedgerReceipts from "./pages/trust-ledger/Receipts";
import TrustLedgerReceiptForm from "./pages/trust-ledger/ReceiptForm";
import TrustLedgerStatement from "./pages/trust-ledger/Statement";
import TrustLedgerFinancialStatement from "./pages/trust-ledger/FinancialStatement";
import TrustLedgerSetup from "./pages/trust-ledger/Setup";
import BuyHistoryReport from "./pages/reports/BuyHistoryReport";
import CryptoBoxEliteSalesReport from "./pages/reports/CryptoBoxEliteSalesReport";
import VPNEliteSalesReport from "./pages/reports/VPNEliteSalesReport";
import LicenseHistoryReport from "./pages/reports/LicenseHistoryReport";
import ServiceHistoryReport from "./pages/reports/ServiceHistoryReport";
import CryptoBoxHistoryReport from "./pages/reports/CryptoBoxHistoryReport";
import BuyBFMReport from "./pages/reports/BuyBFMReport";
import GeneralIncomeHistory from "./pages/reports/GeneralIncomeHistory";
import UserSalesReport from "./pages/reports/UserSalesReport";
import EliteIncomeReport from "./pages/reports/EliteIncomeReport";
import SalesReport from "./pages/reports/SalesReport";
import ElitesList from "./pages/elites/ElitesList";
import UserProfile from "./pages/users/UserProfile";
import GUSDDepositHistory from "./pages/deposits/GUSDDepositHistory";
import CreditsDepositPerUser from "./pages/deposits/CreditsDepositPerUser";
import CreditsDepositHistory from "./pages/deposits/CreditsDepositHistory";
import GUSDDepositSweep from "./pages/deposits/GUSDDepositSweep";
import GUSDWithdrawals from "./pages/withdrawals/GUSDWithdrawals";
import BFMWithdrawals from "./pages/withdrawals/BFMWithdrawals";
import BoxLottery from "./pages/lottery/BoxLottery";
import LotteryTickets from "./pages/lottery/LotteryTickets";
import DailyLottery from "./pages/lottery/DailyLottery";
import DailyLotteryParticipants from "./pages/lottery/DailyLotteryParticipants";
import SupportLogin from "./pages/support/SupportLogin";
import AnnouncementPage from "./pages/announcement/AnnouncementPage";
import SupportSystemLogin from "./pages/support-system/login";
import SupportSystemDashboard from "./pages/support-system/dashboard";
// import SupportSystemTickets from "./pages/support-system/tickets";
import SupportSystemAgents from "./pages/support-system/agent-dashboard";
import TelegramSetup from "./pages/support-system/telegram-setup";
import WidgetIntegration from "./pages/support-system/widget-integration";
import WidgetDemo from "./pages/support-system/widget-demo";
import { MiningAuthProvider } from "./context/MiningAuthContext";
import MiningLogin from "./pages/mining/MiningLogin";
import MiningDashboard from "./pages/mining/MiningDashboard";
import MiningLayout from "./components/MiningLayout";
import SupportLayout from "./components/SupportLayout";
import MiningActiveUsers from "./pages/mining/MiningActiveUsers";
import MiningInactiveUsers from "./pages/mining/MiningInactiveUsers";
import MiningActiveMiners from "./pages/mining/MiningActiveMiners";
import MiningLeaderboard from "./pages/mining/MiningLeaderboard";
import MiningIncome from "./pages/mining/MiningIncome";
import MiningWallet from "./pages/mining/MiningWallet";
import MiningSocial from "./pages/mining/MiningSocial";
import WithdrawRequests from "./pages/mining/WithdrawRequests";
import WithdrawHistory from "./pages/mining/WithdrawHistory";
import EnergyCards from "./pages/mining/EnergyCards";
import MinedTokens from "./pages/mining/MinedTokens";
import Wallets from "./pages/mining/Wallets";
import { SupportDashboard } from "./pages/support-system/support-dashboard";
import ArticlesKBManagement from "./pages/support-system/articles-kb-management";
import CategoriesManagement from "./pages/support-system/categories-management";
import SavedReplies from "./pages/support-system/saved-replies";
import AdminAccess from "./pages/support-system/admin-access";
import ActivityLog from "./pages/support-system/activity-log";
import ArticleTaxonomy from "./pages/support-system/article-taxonomy";
import WorkflowTags from "./pages/support-system/workflow-tags";
import ArticleFeedback from "./pages/support-system/article-feedback";
import HelpCenterAnalytics from "./pages/support-system/analytics";
import FeedbackManagement from "./pages/support-system/feedback";
import LiveChatFeedback from "./pages/support-system/LiveChatFeedback";
import GidexFeesPage from "./pages/gidex-fees/GidexFeesPage";
import GinoxAIStatus from "./pages/ginox-ai/GinoxAIStatus";
import AIPayments from "./pages/ginox-ai/AIPayments";
import ValuesPage from "./pages/values/ValuesPage";
import RebateSettings from "./pages/settings/RebateSettings";
import SignalXList from "./pages/services/SignalXList";
import SignalXStats from "./pages/services/SignalXStats";
import IPVPNList from "./pages/services/IPVPNList";
import IPVPNStats from "./pages/services/IPVPNStats";
import AppCreditStats from "./pages/credit-stats/AppCreditStats";
import UserCreditStats from "./pages/credit-stats/UserCreditStats";
import CreditsUsageHistory from "./pages/credit-stats/CreditsUsageHistory";

// Web3 / Gidex imports
import { AdminShell } from "./layouts/AdminShell";
import { FillsPage } from "./features/web3/hyperliquid/pages/FillsPage";
import { FeesPage } from "./features/web3/hyperliquid/pages/FeesPage";
import { FillsDrilldownPage } from "./features/web3/hyperliquid/pages/FillsDrilldownPage";
import { CombinedDashboard } from "./features/web3/combined/pages/CombinedDashboard";
import { WithdrawalQueuePage as ForexWithdrawalQueuePage } from "./features/web3/forex/pages/WithdrawalQueuePage";
import IdentityVerification from "./pages/kyc/IdentityVerification";
import Faqs from "./pages/faqs/Faqs";
import Blogs from "./pages/blogs/Blogs";
import BlogForm from "./pages/blogs/BlogForm";
import IbRequests from "./pages/forex-ib/IbRequests";
import HeldIncome from "./pages/forex-ib/HeldIncome";
import Transfers from "./pages/forex/Transfers";
import ForexAdmin from "./pages/forex-admin/ForexAdmin";
import BdVerification from "./pages/users/BdVerification";
import { DepositQueuePage as ForexDepositQueuePage } from "./features/web3/forex/pages/DepositQueuePage";
import { DepositSweepPage as ForexDepositSweepPage } from "./features/web3/forex/pages/DepositSweepPage";
import Web3Login from "./features/web3/pages/Web3Login";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";

// Set up token getter for axios
setTokenGetter(() => store.getState().auth.access_token);

// A moved page's old URL, keeping its ?login= / ?username= filter.
function RedirectKeepingQuery({ to }) {
  return <Navigate to={to + useLocation().search} replace />;
}

function AppRoutes() {
  const { user } = useSelector((state) => state.auth);
  return (
    <Routes>
      <Route
        path="/auth/login"
        element={
          user ? (
            <Navigate to="/" />
          ) : (
            <PublicLayout>
              <Login />
            </PublicLayout>
          )
        }
      />
      <Route
        path="/auth/reset-password"
        element={
          user ? (
            <Navigate to="/" />
          ) : (
            <PublicLayout>
              <ResetPassword />
            </PublicLayout>
          )
        }
      />
      <Route
        path="/"
        element={
          user ? (
            <Layout>
              <Dashboard />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/roles"
        element={
          user ? (
            <Layout>
              <RolesList />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/roles/create"
        element={
          user ? (
            <Layout>
              <CreateRole />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/roles/edit/:id"
        element={
          user ? (
            <Layout>
              <EditRole />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/profile"
        element={
          user ? (
            <Layout>
              <UpdateProfile />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/profile/password"
        element={
          user ? (
            <Layout>
              <UpdatePassword />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/profile/2fa"
        element={
          user ? (
            <Layout>
              <TwoFactorAuth />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/profile/validate-tg"
        element={
          user ? (
            <Layout>
              <ValidateTelegram />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/moderators"
        element={
          user ? (
            <Layout>
              <ModeratorsList />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/moderators/add"
        element={
          user ? (
            <Layout>
              <AddModerator />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/moderators/edit/:id"
        element={
          user ? (
            <Layout>
              <EditModerator />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/moderators/password/:id"
        element={
          user ? (
            <Layout>
              <UpdateModeratorPassword />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users"
        element={
          user ? (
            <Layout>
              <UsersList />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/add"
        element={
          user ? (
            <Layout>
              <AddUser />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/update-password/:username"
        element={
          user ? (
            <Layout>
              <UpdateUserPassword />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/view-status"
        element={
          user ? (
            <Layout>
              <ViewUserStatus />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/:username/income"
        element={
          user ? (
            <Layout>
              <UserIncome />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/:username/statement"
        element={
          user ? (
            <Layout>
              <UserStatement />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/:username/stats"
        element={
          user ? (
            <Layout>
              <UserStats />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/edit/:username"
        element={
          user ? (
            <Layout>
              <EditUser />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/:username/sponsor-bonus-report"
        element={
          user ? (
            <Layout>
              <SponsorBonusReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/:username/wallet-statement-report"
        element={
          user ? (
            <Layout>
              <WalletStatementReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/:username/profile"
        element={
          user ? (
            <Layout>
              <UserProfile />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/sponsor-bonus"
        element={
          user ? (
            <Layout>
              <SponsorBonusReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/sponsor-bonus-level-wise"
        element={
          user ? (
            <Layout>
              <SponsorBonusLevelWiseReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/wallet-statement"
        element={
          user ? (
            <Layout>
              <WalletStatementReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/buy-history"
        element={
          user ? (
            <Layout>
              <BuyHistoryReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/buy-bfm"
        element={
          user ? (
            <Layout>
              <BuyBFMReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/cryptobox-elite-sales"
        element={
          user ? (
            <Layout>
              <CryptoBoxEliteSalesReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/vpn-elite-sales"
        element={
          user ? (
            <Layout>
              <VPNEliteSalesReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/license-history"
        element={
          user ? (
            <Layout>
              <LicenseHistoryReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/service-history"
        element={
          user ? (
            <Layout>
              <ServiceHistoryReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/cryptobox-history"
        element={
          user ? (
            <Layout>
              <CryptoBoxHistoryReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/income-history"
        element={
          user ? (
            <Layout>
              <GeneralIncomeHistory />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/user-sales"
        element={
          user ? (
            <Layout>
              <UserSalesReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/elite-income"
        element={
          user ? (
            <Layout>
              <EliteIncomeReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/reports/sales-report"
        element={
          user ? (
            <Layout>
              <SalesReport />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/wallets/gusd"
        element={
          user ? (
            <Layout>
              <GUSDWallets />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/wallets/bfm"
        element={
          user ? (
            <Layout>
              <BFMWallets />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/wallets/app-credit"
        element={
          user ? (
            <Layout>
              <APPCreditWallets />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/wallets/forex"
        element={
          user ? (
            <Layout>
              {/* key forces a remount when switching from /wallets/gusd, which renders the same component */}
              <GUSDWallets key="FOREX" type="FOREX" />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/wallets/adjustments"
        element={
          user ? (
            <Layout>
              <BalanceAdjustments />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/trust-ledger/receipts"
        element={
          user ? (
            <Layout>
              <TrustLedgerReceipts />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/trust-ledger/receipts/new"
        element={
          user ? (
            <Layout>
              <TrustLedgerReceiptForm />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/trust-ledger/receipts/:id/edit"
        element={
          user ? (
            <Layout>
              <TrustLedgerReceiptForm />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/trust-ledger/statement"
        element={
          user ? (
            <Layout>
              <TrustLedgerStatement />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/trust-ledger/financial-statements"
        element={
          user ? (
            <Layout>
              <TrustLedgerFinancialStatement />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/trust-ledger/setup"
        element={
          user ? (
            <Layout>
              <TrustLedgerSetup />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/deposits/list"
        element={
          user ? (
            <Layout>
              <GUSDDepositHistory />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/deposits/credits-per-user"
        element={
          user ? (
            <Layout>
              <CreditsDepositPerUser />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/deposits/credits-history"
        element={
          user ? (
            <Layout>
              <CreditsDepositHistory />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/deposits/gusd-sweep"
        element={
          user ? (
            <Layout>
              <GUSDDepositSweep />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/kyc/identity"
        element={
          user ? (
            <Layout>
              <IdentityVerification />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      {/* Risk Reports and Client Book moved into Forex Admin; old links still land. */}
      <Route path="/forex/risk" element={<Navigate to="/forex-admin/risk" replace />} />
      <Route path="/forex/risk/account" element={<RedirectKeepingQuery to="/forex-admin/risk/account" />} />
      <Route path="/forex/clients" element={<Navigate to="/forex-admin/clients" replace />} />
      <Route
        path="/forex/transfers"
        element={
          user ? (
            <Layout>
              <Transfers />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/forex-admin/login"
        element={
          user ? (
            <Navigate to="/forex-admin" />
          ) : (
            <PublicLayout>
              <Login app="Forex" />
            </PublicLayout>
          )
        }
      />
      {/* Forex Admin is its own app with its own shell, like Gidex. */}
      <Route
        path="/forex-admin/*"
        element={user ? <ForexAdmin /> : <Navigate to="/forex-admin/login" />}
      />
      <Route
        path="/forex-ib/requests"
        element={
          user ? (
            <Layout>
              <IbRequests />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/forex-ib/held-income"
        element={
          user ? (
            <Layout>
              <HeldIncome />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/users/bd-verification"
        element={
          user ? (
            <Layout>
              <BdVerification />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/withdrawals/gusd"
        element={
          user ? (
            <Layout>
              <GUSDWithdrawals />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/withdrawals/bfm"
        element={
          user ? (
            <Layout>
              <BFMWithdrawals />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/services/signalx/list"
        element={
          user ? (
            <Layout>
              <SignalXList />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/services/signalx/stats"
        element={
          user ? (
            <Layout>
              <SignalXStats />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/services/ipvpn/list"
        element={
          user ? (
            <Layout>
              <IPVPNList />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/services/ipvpn/stats"
        element={
          user ? (
            <Layout>
              <IPVPNStats />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/lottery/box"
        element={
          user ? (
            <Layout>
              <BoxLottery />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/lottery/box/:lotteryId/tickets"
        element={
          user ? (
            <Layout>
              <LotteryTickets />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/lottery/daily"
        element={
          user ? (
            <Layout>
              <DailyLottery />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/lottery/daily/:lotteryId/participants"
        element={
          user ? (
            <Layout>
              <DailyLotteryParticipants />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/elites"
        element={
          user ? (
            <Layout>
              <ElitesList />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />

      <Route
        path="/support/SupportLogin"
        element={
          user ? (
            <Layout>
              <SupportLogin />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/announcement/AnnouncementPage"
        element={
          user ? (
            <Layout>
              <AnnouncementPage />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/faqs"
        element={
          user ? (
            <Layout>
              <Faqs />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/blogs"
        element={
          user ? (
            <Layout>
              <Blogs />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      {["/blogs/new", "/blogs/:id/edit"].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            user ? (
              <Layout>
                <BlogForm />
              </Layout>
            ) : (
              <Navigate to="/auth/login" />
            )
          }
        />
      ))}

      <Route
        path="/gidex-fees"
        element={
          user ? (
            <Layout>
              <GidexFeesPage />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />

      <Route
        path="/ginox-ai/status"
        element={
          user ? (
            <Layout>
              <GinoxAIStatus />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />

      <Route
        path="/ginox-ai/payments"
        element={
          user ? (
            <Layout>
              <AIPayments />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />

      <Route
        path="/values"
        element={
          user ? (
            <Layout>
              <ValuesPage />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />

      <Route
        path="/settings/rebate"
        element={
          user ? (
            <Layout>
              <RebateSettings />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />

      <Route
        path="/credit-stats/app"
        element={
          user ? (
            <Layout>
              <AppCreditStats />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/credit-stats/user"
        element={
          user ? (
            <Layout>
              <UserCreditStats />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/credit-stats/user/:username"
        element={
          user ? (
            <Layout>
              <UserCreditStats />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />
      <Route
        path="/credit-stats/usage"
        element={
          user ? (
            <Layout>
              <CreditsUsageHistory />
            </Layout>
          ) : (
            <Navigate to="/auth/login" />
          )
        }
      />

      {/* Support System Routes */}
      <Route path="/support-system/login" element={<SupportSystemLogin />} />
      <Route path="/support-system/dashboard" element={<SupportLayout><SupportSystemDashboard /></SupportLayout>} />
      <Route path="/support-system/tickets" element={<SupportLayout><SupportDashboard /></SupportLayout>} />
      <Route path="/support-system/articles-kb" element={<SupportLayout><ArticlesKBManagement /></SupportLayout>} />
      <Route path="/support-system/categories" element={<SupportLayout><CategoriesManagement /></SupportLayout>} />
      <Route path="/support-system/saved-replies" element={<SupportLayout><SavedReplies /></SupportLayout>} />
      <Route path="/support-system/admin-access" element={<SupportLayout><AdminAccess /></SupportLayout>} />
      <Route path="/support-system/activity-log" element={<SupportLayout><ActivityLog /></SupportLayout>} />
      <Route path="/support-system/article-taxonomy" element={<SupportLayout><ArticleTaxonomy /></SupportLayout>} />
      <Route path="/support-system/workflow-tags" element={<SupportLayout><WorkflowTags /></SupportLayout>} />
      <Route path="/support-system/article-feedback" element={<SupportLayout><ArticleFeedback /></SupportLayout>} />
      <Route path="/support-system/analytics" element={<SupportLayout><HelpCenterAnalytics /></SupportLayout>} />
      <Route path="/support-system/feedback" element={<SupportLayout><FeedbackManagement /></SupportLayout>} />
      <Route path="/support-system/live-chat-feedback" element={<SupportLayout><LiveChatFeedback /></SupportLayout>} />
      <Route path="/support-system/agents" element={<SupportLayout><SupportSystemAgents /></SupportLayout>} />
      <Route path="/support-system/telegram-setup" element={<SupportLayout><TelegramSetup /></SupportLayout>} />
      <Route path="/support-system/widget-integration" element={<SupportLayout><WidgetIntegration /></SupportLayout>} />
      <Route path="/widget-demo" element={<WidgetDemo />} />

      {/* Mining Admin Routes */}
      <Route path="/mining/login" element={<MiningLogin />} />
      <Route
        path="/mining/dashboard"
        element={
          <MiningLayout>
            <MiningDashboard />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/active-users"
        element={
          <MiningLayout>
            <MiningActiveUsers />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/inactive-users"
        element={
          <MiningLayout>
            <MiningInactiveUsers />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/active-miners"
        element={
          <MiningLayout>
            <MiningActiveMiners />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/leaderboard"
        element={
          <MiningLayout>
            <MiningLeaderboard />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/income"
        element={
          <MiningLayout>
            <MiningIncome />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/wallet"
        element={
          <MiningLayout>
            <MiningWallet />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/social"
        element={
          <MiningLayout>
            <MiningSocial />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/withdrawables"
        element={
          <MiningLayout>
            <WithdrawRequests />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/withdraw-history"
        element={
          <MiningLayout>
            <WithdrawHistory />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/energy-cards"
        element={
          <MiningLayout>
            <EnergyCards />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/mined-tokens"
        element={
          <MiningLayout>
            <MinedTokens />
          </MiningLayout>
        }
      />
      <Route
        path="/mining/wallets"
        element={
          <MiningLayout>
            <Wallets />
          </MiningLayout>
        }
      />

      {/* Web3 Admin Routes - NOW WITH LOGIN PROTECTION */}
      <Route
        path="/web3/login"
        element={user ? <Navigate to="/web3/combined" /> : <Web3Login />}
      />
      <Route
        path="/web3"
        element={user ? <AdminShell /> : <Navigate to="/web3/login" />}
      >
        <Route path="combined" element={<CombinedDashboard />} />
        <Route path="hyperliquid/fills" element={<FillsPage />} />
        <Route path="hyperliquid/fees" element={<FeesPage />} />
        <Route
          path="hyperliquid/fills/:walletAddress"
          element={<FillsDrilldownPage />}
        />
        <Route
          path="forex/withdrawals"
          element={<ForexWithdrawalQueuePage />}
        />
        <Route path="forex/deposits" element={<ForexDepositQueuePage />} />
        <Route path="forex/sweep" element={<ForexDepositSweepPage />} />
      </Route>

      {/* Add more protected routes here */}
    </Routes>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <MiningAuthProvider>
            <Router basename={process.env.PUBLIC_URL || ""}>
              <AppRoutes />
            </Router>
            <ToastContainer position="top-right" autoClose={3000} />
          </MiningAuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </PersistGate>
  </Provider>,
);
